import { AdminLayout } from "../../components/layout/AdminLayout";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { MessageSquare, Clock, Flame, ThermometerSun, Snowflake, Send, User, Building2, Users, Calendar } from "lucide-react";
import api from "../../lib/api";


const priorityConfig = { hot: { icon: Flame, color: "#C7322A", bg: "#FDE8E7", label: "Hot" }, warm: { icon: ThermometerSun, color: "#D97706", bg: "#FEF6E4", label: "Warm" }, cool: { icon: Snowflake, color: "#1B4F8A", bg: "#ddeaf9", label: "Cool" } };
const statusConfig = { open: { color: "#F5841F", bg: "#FEF0E0" }, replied: { color: "#1B4F8A", bg: "#ddeaf9" }, converted: { color: "#0E8A55", bg: "#D4F2E7" }, closed: { color: "#7A90A8", bg: "rgba(11,21,32,0.08)" } };

const QuoteRequestsPage = () => {
  const { token } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterPriority) params.set("priority", filterPriority);
      const res = await api.get("/quote-requests?${params}");
      setQuotes(res.data.quotes || []);
      setStats(res.data.stats || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, filterStatus, filterPriority]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateQuote = async (id, updates) => {
    try {
      await api.patch("/quote-requests/${id}", updates);
      toast.success("Quote updated");
      fetchData();
    } catch (e) { toast.error("Failed to update"); }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    await updateQuote(selected.id, { reply_message: replyText });
    setReplyText("");
    const res = await api.get("/quote-requests/${selected.id}");
    setSelected(res.data);
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}</div>;

  return (
    <AdminLayout title="Quote Requests" variant="super_admin">
    <div className="p-6 space-y-6" data-testid="quote-requests-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Quote Requests</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage ILT session quote requests from prospective clients</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { label: "Total", value: stats.total || 0, color: "#1B4F8A" },
          { label: "Open", value: stats.open || 0, color: "#F5841F" },
          { label: "Replied", value: stats.replied || 0, color: "#1B4F8A" },
          { label: "Converted", value: stats.converted || 0, color: "#0E8A55" },
          { label: "Hot Leads", value: stats.hot || 0, color: "#C7322A" },
        ].map((s, i) => (
          <div key={i} className="bg-background border rounded-xl p-3" style={{ borderTop: `3px solid ${s.color}` }}>
            <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">{s.label}</p>
            <p className="text-xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <select className="h-9 rounded-md border px-3 text-sm bg-background" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="replied">Replied</option>
          <option value="converted">Converted</option>
          <option value="closed">Closed</option>
        </select>
        <select className="h-9 rounded-md border px-3 text-sm bg-background" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
          <option value="">All Priority</option>
          <option value="hot">Hot</option>
          <option value="warm">Warm</option>
          <option value="cool">Cool</option>
        </select>
      </div>

      <div className="flex gap-6">
        {/* List */}
        <div className="flex-1 border rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Requestor</th>
                <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Company</th>
                <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Session</th>
                <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Learners</th>
                <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Priority</th>
                <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {quotes.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="font-medium">No quote requests yet</p>
                </td></tr>
              ) : quotes.map(q => {
                const pc = priorityConfig[q.priority] || priorityConfig.cool;
                const sc = statusConfig[q.status] || statusConfig.open;
                const PIcon = pc.icon;
                return (
                  <tr key={q.id} className={`border-t cursor-pointer transition-colors ${selected?.id === q.id ? 'bg-orange-50' : 'hover:bg-muted/30'}`} onClick={() => setSelected(q)} data-testid={`quote-row-${q.id}`}>
                    <td className="p-3">
                      <p className="font-semibold">{q.requestor_name}</p>
                      <p className="text-xs text-muted-foreground">{q.requestor_email}</p>
                    </td>
                    <td className="p-3 text-muted-foreground">{q.company}</td>
                    <td className="p-3 text-muted-foreground max-w-[150px] truncate">{q.session_requested}</td>
                    <td className="p-3">{q.learner_count}</td>
                    <td className="p-3"><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase" style={{ background: pc.bg, color: pc.color }}><PIcon className="w-3 h-3" />{pc.label}</span></td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase" style={{ background: sc.bg, color: sc.color }}>{q.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Detail Panel */}
        {selected && (
          <div className="w-[380px] border rounded-xl p-4 space-y-4 bg-background shadow-sm" data-testid="quote-detail-panel">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg" style={{ fontFamily: "'Sora', sans-serif" }}>{selected.requestor_name}</h3>
              <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-foreground text-sm">Close</button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2"><User className="w-4 h-4 text-muted-foreground" />{selected.requestor_email}</div>
              <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-muted-foreground" />{selected.company}</div>
              <div className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-muted-foreground" />{selected.session_requested}</div>
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-muted-foreground" />{selected.learner_count} learners</div>
              {selected.preferred_dates && <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-muted-foreground" />{selected.preferred_dates}</div>}
              {selected.notes && <p className="bg-muted/50 p-2 rounded text-muted-foreground text-xs">{selected.notes}</p>}
            </div>
            <div className="flex gap-2">
              <label className="text-xs font-medium">Priority:</label>
              {["hot", "warm", "cool"].map(p => {
                const pc = priorityConfig[p]; const PIcon = pc.icon;
                return <button key={p} onClick={() => updateQuote(selected.id, { priority: p })} className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase flex items-center gap-1 ${selected.priority === p ? 'ring-2 ring-offset-1' : ''}`} style={{ background: pc.bg, color: pc.color }}><PIcon className="w-3 h-3" />{pc.label}</button>;
              })}
            </div>
            <div className="flex gap-2">
              <label className="text-xs font-medium">Status:</label>
              {["open", "replied", "converted", "closed"].map(s => {
                const sc = statusConfig[s];
                return <button key={s} onClick={() => updateQuote(selected.id, { status: s })} className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase ${selected.status === s ? 'ring-2 ring-offset-1' : ''}`} style={{ background: sc.bg, color: sc.color }}>{s}</button>;
              })}
            </div>

            {/* Replies */}
            <div className="border-t pt-3 space-y-2">
              <h4 className="text-sm font-bold">Replies</h4>
              {(selected.replies || []).map(r => (
                <div key={r.id} className="bg-blue-50 p-2 rounded text-sm">
                  <p className="font-medium text-xs text-blue-800">{r.sent_by_name} · {new Date(r.sent_at).toLocaleDateString()}</p>
                  <p className="text-sm mt-1">{r.message}</p>
                </div>
              ))}
              <div className="flex gap-2">
                <Input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type a reply..." className="flex-1" data-testid="quote-reply-input" />
                <Button size="sm" className="btn-primary" onClick={sendReply} data-testid="quote-reply-send"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </AdminLayout>
  );
};

export default QuoteRequestsPage;
